import React, { useEffect, useRef } from 'react';

const NeuralNetworkBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let neurons = [];
    let synapses = [];
    let mouse = { x: null, y: null, active: false };
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

      class Neuron {
        constructor() {
          this.x = Math.random() * canvas.width;
          this.y = Math.random() * canvas.height;
          this.vx = (Math.random() - 0.5) * 0.8;
          this.vy = (Math.random() - 0.5) * 0.8;
          this.radius = Math.random() * 3 + 2;
          this.pulsePhase = Math.random() * Math.PI * 2;
          this.brightness = Math.random() * 0.15 + 0.05;
          this.layer = Math.floor(Math.random() * 3);
          this.active = false;
          this.activation = 0;
          this.isYellow = Math.random() > 0.92;
        }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;

        this.pulsePhase += 0.03;
        
        let distToMouse = Infinity;
        if (mouse.active) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          distToMouse = Math.sqrt(dx * dx + dy * dy);
        }

        if (distToMouse < 250) {
          this.active = true;
          this.activation = Math.min(1, this.activation + 0.15);
        } else {
          this.activation = Math.max(0, this.activation - 0.02);
        }

        if (this.activation > 0.5) {
          this.active = true;
        }
      }

      draw() {
        const pulse = Math.sin(this.pulsePhase) * 0.3 + 0.7;
        const activationGlow = this.activation * 0.8;
        
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.radius * 8
        );
        
        const purpleBase = this.isYellow ? 'rgba(255, 215, 0,' : 'rgba(199, 125, 255,';
        const purpleMid = this.isYellow ? 'rgba(255, 215, 0,' : 'rgba(157, 78, 221,';
        const purpleDark = this.isYellow ? 'rgba(255, 215, 0,' : 'rgba(123, 44, 191,';
        
        if (this.active) {
          gradient.addColorStop(0, `${purpleBase} ${0.9 * pulse + activationGlow})`);
          gradient.addColorStop(0.3, `${purpleMid} ${0.6 * pulse + activationGlow * 0.5})`);
          gradient.addColorStop(0.6, `${purpleDark} ${0.3 * pulse + activationGlow * 0.3})`);
          gradient.addColorStop(1, 'rgba(74, 0, 128, 0)');
        } else {
          gradient.addColorStop(0, `${purpleMid} ${this.brightness * pulse})`);
          gradient.addColorStop(0.5, `rgba(74, 0, 128, ${this.brightness * pulse * 0.5})`);
          gradient.addColorStop(1, 'rgba(74, 0, 128, 0)');
        }

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius * 8, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = this.active ? 
          `rgba(255, 255, 255, ${0.9 + this.activation * 0.1})` : 
          this.isYellow ? 
            `rgba(255, 215, 0, ${0.7 * pulse})` : 
            `rgba(199, 125, 255, ${0.7 * pulse})`;
        ctx.fill();
      }
    }

    class Synapse {
      constructor(neuronA, neuronB) {
        this.neuronA = neuronA;
        this.neuronB = neuronB;
        this.strength = Math.random() * 0.25 + 0.05;
        this.activeStrength = 0;
        this.pulseDirection = Math.random() > 0.5 ? 1 : -1;
        this.pulsePosition = Math.random();
        this.isYellow = neuronA.isYellow || neuronB.isYellow;
      }

      update() {
        const distance = Math.sqrt(
          Math.pow(this.neuronA.x - this.neuronB.x, 2) + 
          Math.pow(this.neuronA.y - this.neuronB.y, 2)
        );

        if (distance < 200 && (this.neuronA.active || this.neuronB.active)) {
          this.activeStrength = Math.min(1, this.activeStrength + 0.1);
        } else {
          this.activeStrength = Math.max(0, this.activeStrength - 0.03);
        }

        this.pulsePosition += 0.02 * this.pulseDirection;
        if (this.pulsePosition > 1) this.pulsePosition = 0;
        if (this.pulsePosition < 0) this.pulsePosition = 1;
      }

      draw() {
        const distance = Math.sqrt(
          Math.pow(this.neuronA.x - this.neuronB.x, 2) + 
          Math.pow(this.neuronA.y - this.neuronB.y, 2)
        );

        if (distance > 250) return;

        const opacity = (1 - distance / 250) * (this.strength + this.activeStrength * 0.8);
        
        if (opacity < 0.02) return;

        const gradient = ctx.createLinearGradient(
          this.neuronA.x, this.neuronA.y,
          this.neuronB.x, this.neuronB.y
        );

        const yellowBase = this.isYellow ? 'rgba(255, 215, 0,' : 'rgba(199, 125, 255,';
        
        if (this.activeStrength > 0.3) {
          gradient.addColorStop(0, `${yellowBase} ${opacity})`);
          gradient.addColorStop(0.5, this.isYellow ? `rgba(255, 215, 0, ${opacity * 0.8})` : `rgba(123, 44, 191, ${opacity * 0.8})`);
          gradient.addColorStop(1, `${yellowBase} ${opacity})`);
        } else {
          gradient.addColorStop(0, this.isYellow ? `rgba(255, 215, 0, ${opacity * 0.4})` : `rgba(74, 0, 128, ${opacity * 0.6})`);
          gradient.addColorStop(0.5, this.isYellow ? `rgba(255, 215, 0, ${opacity * 0.3})` : `rgba(157, 78, 221, ${opacity * 0.4})`);
          gradient.addColorStop(1, this.isYellow ? `rgba(255, 215, 0, ${opacity * 0.4})` : `rgba(74, 0, 128, ${opacity * 0.6})`);
        }

        ctx.beginPath();
        ctx.moveTo(this.neuronA.x, this.neuronA.y);
        
        const midX = (this.neuronA.x + this.neuronB.x) / 2;
        const midY = (this.neuronA.y + this.neuronB.y) / 2;
        const curve = (this.neuronA.x - this.neuronB.x) * (this.neuronA.y - this.neuronB.y) * 0.0001;
        
        ctx.quadraticCurveTo(midX + curve * 50, midY + curve * 50, this.neuronB.x, this.neuronB.y);
        
        ctx.strokeStyle = gradient;
        ctx.lineWidth = this.activeStrength > 0.5 ? 1.5 : 0.8;
        ctx.stroke();

        if (this.activeStrength > 0.5) {
          const pulseX = this.neuronA.x + (this.neuronB.x - this.neuronA.x) * this.pulsePosition;
          const pulseY = this.neuronA.y + (this.neuronB.y - this.neuronA.y) * this.pulsePosition;
          
          const pulseGradient = ctx.createRadialGradient(
            pulseX, pulseY, 0,
            pulseX, pulseY, 8
          );
          pulseGradient.addColorStop(0, this.isYellow ? `rgba(255, 215, 0, ${this.activeStrength})` : `rgba(199, 125, 255, ${this.activeStrength})`);
          pulseGradient.addColorStop(1, this.isYellow ? 'rgba(255, 215, 0, 0)' : 'rgba(199, 125, 255, 0)');
          
          ctx.beginPath();
          ctx.arc(pulseX, pulseY, 8, 0, Math.PI * 2);
          ctx.fillStyle = pulseGradient;
          ctx.fill();
        }
      }
    }

    const createNetwork = () => {
      neurons = [];
      synapses = [];
      
      const neuronCount = Math.min(60, Math.floor((canvas.width * canvas.height) / 16000));
      
      for (let i = 0; i < neuronCount; i++) {
        neurons.push(new Neuron());
      }

      for (let i = 0; i < neurons.length; i++) {
        const nearbyNeurons = neurons
          .map((n, idx) => ({ neuron: n, idx }))
          .filter((n, idx) => idx !== i)
          .map(n => ({
            ...n,
            distance: Math.sqrt(
              Math.pow(neurons[i].x - n.neuron.x, 2) + 
              Math.pow(neurons[i].y - n.neuron.y, 2)
            )
          }))
          .filter(n => n.distance < 200)
          .sort((a, b) => a.distance - b.distance)
          .slice(0, 4);

        nearbyNeurons.forEach(n => {
          const exists = synapses.some(s => 
            (s.neuronA === neurons[i] && s.neuronB === n.neuron) ||
            (s.neuronB === neurons[i] && s.neuronA === n.neuron)
          );
          if (!exists) {
            synapses.push(new Synapse(neurons[i], n.neuron));
          }
        });
      }
    };

    const drawDataFlow = () => {
      if (!mouse.active) return;

      neurons.forEach(neuron => {
        const dx = mouse.x - neuron.x;
        const dy = mouse.y - neuron.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 150) {
          const intensity = (1 - distance / 150);
          
          ctx.beginPath();
          ctx.moveTo(mouse.x, mouse.y);
          ctx.lineTo(neuron.x, neuron.y);
          ctx.strokeStyle = neuron.isYellow ? 
            `rgba(255, 215, 0, ${intensity * 0.3})` : 
            `rgba(199, 125, 255, ${intensity * 0.3})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      });
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time++;

      synapses.forEach(synapse => {
        synapse.update();
        synapse.draw();
      });

      neurons.forEach(neuron => {
        neuron.update();
        neuron.draw();
      });

      drawDataFlow();

      animationFrameId = requestAnimationFrame(draw);
    };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
    };

    const handleMouseLeave = () => {
      mouse.active = false;
    };

    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        mouse.x = e.touches[0].clientX;
        mouse.y = e.touches[0].clientY;
        mouse.active = true;
      }
    };

    const handleTouchEnd = () => {
      mouse.active = false;
    };

    resizeCanvas();
    createNetwork();
    draw();

    window.addEventListener('resize', () => {
      resizeCanvas();
      createNetwork();
    });

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(5, 5, 7, 0.65)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />
    </>
  );
};

export default NeuralNetworkBackground;
